import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './AdminPanel.css';

function AdminPanel() {
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({
    category: '',
    prompt: '',
    type: 'text',
    guidance: '',
    isNoteField: false,
    options: []
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  const fetchQuestions = async () => {
    const res = await axios.get('https://api.discovery.quantecsystems.com/questions');
    setQuestions(res.data.map(q => ({ ...q, expanded: false })));
    setSelectedIds([]);
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...form.options];
    newOptions[index] = value;
    setForm(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setForm(prev => ({ ...prev, options: [...prev.options, ''] }));
  };

  const removeOption = (index) => {
    const newOptions = [...form.options];
    newOptions.splice(index, 1);
    setForm(prev => ({ ...prev, options: newOptions }));
  };

  const handleSubmit = async () => {
    const payload = {
      category: form.category,
      prompt: form.prompt,
      type: form.type,
      options: form.options?.filter(Boolean),
      meta: {
        guidance: form.guidance,
        isNoteField: form.isNoteField
      }
    };

    if (editMode) {
      await axios.put(`https://api.discovery.quantecsystems.com/questions/${editingQuestionId}`, payload);
    } else {
      await axios.post('https://api.discovery.quantecsystems.com/questions', payload);
    }

    resetForm();
    fetchQuestions();
  };

  const handleEdit = () => {
  const q = questions.find(q => q.questionId === selectedIds[0]);
  const typesWithOptions = ['radio', 'multi', 'select', 'yesorno'];
    setForm({
        category: q.category,
        prompt: q.prompt,
        type: q.type,
        guidance: q.meta?.guidance || '',
        isNoteField: q.meta?.isNoteField || false,
        options: typesWithOptions.includes(q.type) ? q.options || [] : []
    });

    setEditMode(true);
    setEditingQuestionId(q.questionId);
    };

  const handleCancel = () => {
    resetForm();
  };

  const resetForm = () => {
    setForm({
      category: '',
      prompt: '',
      type: 'text',
      guidance: '',
      isNoteField: false,
      options: []
    });
    setEditMode(false);
    setEditingQuestionId(null);
    setSelectedIds([]);
  };

  const handleDeleteSelected = async () => {
    for (const id of selectedIds) {
      await axios.delete(`https://api.discovery.quantecsystems.com/questions/${id}`);
    }
    fetchQuestions();
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  const toggleReveal = (id) => {
    setQuestions(prev =>
      prev.map(q => q.questionId === id ? { ...q, expanded: !q.expanded } : q)
    );
  };

  return (
    <div className="admin-container">
      <h2>Admin Panel – Manage Discovery Questions</h2>

      <div className="form-section">
        <input name="category" placeholder="Category" value={form.category} onChange={handleChange} />
        <input name="prompt" placeholder="Question Prompt" value={form.prompt} onChange={handleChange} />
        <select name="type" value={form.type} onChange={handleChange}>
            <option value="text">Text</option>
            <option value="number_input">Number Input</option>
            <option value="yesno">Yes / No</option>
            <option value="select">Select</option>
            <option value="dynamic_list">Dynamic List</option>
            </select>
        <input name="guidance" placeholder="Guidance (optional)" value={form.guidance} onChange={handleChange} />
        <label>
          <input type="checkbox" name="isNoteField" checked={form.isNoteField} onChange={handleChange} />
          Mark as Notes Field
        </label>

        {['select', 'yesno', 'radio', 'multi', 'dynamic_list'].includes(form.type) && (
        <div className="options-section">
            {form.options.map((opt, idx) => (
            <div key={idx}>
                <input
                value={opt}
                onChange={(e) => handleOptionChange(idx, e.target.value)}
                placeholder={`Option ${idx + 1}`}
                />
                <button onClick={() => removeOption(idx)}>X</button>
            </div>
            ))}
            <button onClick={addOption}>+ Add Option</button>
        </div>
        )}
        <div className="form-buttons">
          <button onClick={handleSubmit}>{editMode ? 'Save Changes' : 'Add Question'}</button>
          {editMode && <button onClick={handleCancel}>Cancel</button>}
        </div>
      </div>

      <div className="bulk-actions">
        {selectedIds.length > 0 && (
          <>
            <button onClick={handleDeleteSelected}>Delete Selected</button>
            {selectedIds.length === 1 && <button onClick={handleEdit}>Edit Selected</button>}
          </>
        )}
      </div>

      <div className="question-list">
        <h3>Existing Questions</h3>
        {questions.map(q => (
          <div key={q.questionId} className="question-item">
            <input
              type="checkbox"
              checked={selectedIds.includes(q.questionId)}
              onChange={() => toggleSelection(q.questionId)}
            />
            <strong onClick={() => toggleReveal(q.questionId)} style={{ cursor: 'pointer' }}>
              {q.category}:
            </strong> {q.prompt}
            {q.expanded && (
            <div className="details">
                <p><em>Type:</em> {q.type}</p>

                {q.meta?.guidance && (
                <p><em>Guidance:</em> {q.meta.guidance}</p>
                )}
                {(q.type === 'radio' || q.type === 'multi' || q.type === 'select') && (
                    <div>
                        <p><em>Options:</em></p>
                        <ul style={{ paddingLeft: '20px' }}>
                        {q.options?.length > 0 ? (
                            q.options.map((opt, i) => <li key={i}>{opt}</li>)
                        ) : (
                            <li><em>No options provided</em></li>
                        )}
                        </ul>
                    </div>
                    )}

                {q.meta?.isNoteField && (
                <p><em>This is marked as a notes field.</em></p>
                )}
            </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPanel;
